import { ButtonCus } from '@/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage
} from '@/components/common/Form';
import { Label } from '@/components/common/Label';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';
import Table from '@/components/table/Table';
import {
    DISPPLAY_MOMENT_YMD_HM,
    VEHICLE_ARRANGEMENT_TIME_CONFIG
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { SYSTEM_SETTING_TYPE } from '@/types/enums';
import { PreparationTimeModel } from '@/types/system-setting';
import { formatDateTime } from '@/utils/datetime';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef } from '@tanstack/react-table';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface SettingBikeTimeProps {
    preparationTimeConfig: PreparationTimeModel[]
}

export default observer(function SettingBikeTime({
    preparationTimeConfig = []
}: SettingBikeTimeProps) {
    // hook
    const { t } = useTranslation('translation', {
        keyPrefix: 'system_setting'
    });
    const { t: tAll } = useTranslation();

    // store
    const {
        systemSettingStore: { systemSetting, updateSystemSetting, setObservable },
        modalStore: { showAlertModal }
    } = useStore();

    const validateSchema = yup.object().shape({
        data: yup.array().of(
            yup.object().shape({
                prepareBefore: yup
                    .string()
                    .trim()
                    .checkInputOnlyPositiveDigitsOrEmpty(tAll('form_error_validate.only_number'))
                    // .optional()
                    // .nullable()
                    .required('form_error_validate.required')
            })
        )
    });

    type formData = yup.InferType<typeof validateSchema>;

    const form = useForm<formData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            data: Object.values(VEHICLE_ARRANGEMENT_TIME_CONFIG).map((item) => {
                return ({
                    ...item,
                    prepareBefore: item.prepareBefore.toString()
                });
            })

        }
    });

    // lifecycle
    useEffect(() => {
        setValue(systemSetting);
    }, [systemSetting]);

    const setValue = (systemSetting: any) => {
        let addSrv = [] as any;
        if (systemSetting?.preparationTimeConfig) {
            addSrv = systemSetting?.preparationTimeConfig.map(item => ({
                id: item.id,
                prepareBefore: item.prepareBefore?.toString()
            }));
        }
        form.setValue('data', addSrv);
    };

    // columns
    const columns: ColumnDef<PreparationTimeModel>[] = useMemo(() => {
        return [
            {
                accessorKey: 'rentalHour',
                header: `${t(
                    'setting_bike_arrangement_completion_time.rental_start_time'
                )}`,
                size: 150,
                meta: {
                    headerClassName: 'min-w-[150px]',
                    cellClassName: ''
                },
                enableSorting: false,
                cell: ({ row: { original } }) => (
                    <div>{`${getRentalHour(original.rentalHour)}`}</div>
                )
            },
            {
                accessorKey: 'rentalDate',
                header: `${t(
                    'setting_bike_arrangement_completion_time.bike_rental_date_time'
                )}`,
                size: 150,
                meta: {
                    headerClassName: 'min-w-[147px]',
                    cellClassName: ''
                },
                enableSorting: false,
                cell: ({ row: { original } }) => (
                    <div>
                        {formatDateTime(original.rentalDate, DISPPLAY_MOMENT_YMD_HM, {
                            useLocalTime: false,
                            useServerTime: true
                        })}
                    </div>
                )
            },
            {
                accessorKey: 'prepareBeforeDate',
                header: `${t(
                    'setting_bike_arrangement_completion_time.vehicle_arrangement_date_time'
                )}`,
                size: 150,
                meta: {
                    headerClassName: 'min-w-[147px]',
                    cellClassName: ''
                },
                enableSorting: false,
                cell: ({ row: { original } }) => (
                    <div>
                        {getPrepareBeforeDate(
                            original.rentalDate,
                            Number(original.prepareBefore)
                        )}
                    </div>
                )
            },
            {
                accessorKey: 'prepareBefore',
                header: () => (
                    <Label required>
                        {t('setting_bike_arrangement_completion_time.number_hours_need_arranged_advance')}
                    </Label>
                ),
                size: 180,
                meta: {
                    headerClassName: 'min-w-[180px]',
                    cellClassName: ''
                },
                enableSorting: false,
                cell: ({ row: { original, index } }) => (
                    <div>
                        <FormField
                            control={form.control}
                            name={`data.${index}.prepareBefore`}
                            render={({ field }) => {
                                return (
                                    <FormItem>
                                        <FormControl>
                                            <FormatNumberVer2
                                                decimalScale={0}
                                                maxLength={3}
                                                statusDisplay='input'
                                                thousandSeparator={true}
                                                allowNegative={false}
                                                value={field.value}
                                                setvalueOfNumericFormat={(values) => {
                                                    field.onChange(values);
                                                }}
                                                className='h-8'
                                                suffix='時間'
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                );
                            }}
                        />
                    </div>
                )
            }
        ];
    }, [systemSetting]);

    // function
    const getPrepareBeforeDate = (
        rentalDate: Date | string,
        prepareBefore: number
    ): string => {
        if (!rentalDate) return '';

        const startDate = new Date(rentalDate);
        startDate.setHours(startDate.getHours() - prepareBefore);

        return `${formatDateTime(startDate.toISOString(), DISPPLAY_MOMENT_YMD_HM, {
            useLocalTime: false,
            useServerTime: true
        })}`;
    };

    const getRentalHour = (rentalHour: any): string => {
        const firstHour = 0;
        const lastHour = 23;
        const convertRentalHour = Number(rentalHour);

        if (rentalHour !== firstHour && (!rentalHour || isNaN(convertRentalHour)))
            return '';

        if (convertRentalHour < firstHour || convertRentalHour > lastHour)
            return '';

        return convertRentalHour < 10 ?
            `0${convertRentalHour}:00` :
            `${convertRentalHour}:00`;
    };

    const onSubmit = async (data: formData) => {
        const preparationTimeConfigData = data.data;

        const res = await flowResult(
            updateSystemSetting({
                settingType: SYSTEM_SETTING_TYPE.PREPARATION_TIME,
                data: preparationTimeConfigData
            })
        );

        if (res) {
            setObservable('systemSetting', {
                ...systemSetting,
                preparationTimeConfig: res
            });
            showAlertModal({
                id: 'update-setting-bike-success',
                type: 'success',
                content: tAll('messages.update_success')
            });
        }
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='flex flex-col gap-6 justify-center'
            >
                <div>
                    <Table
                        columns={columns}
                        data={preparationTimeConfig || []}
                        enableRowSelection={false}
                        getRowId={row => row.id?.toString()}
                        showSelectPageSize={false}
                        showTableFooter={false}
                        cellClassName='py-2.5'
                    />
                </div>
                <div className='flex justify-center'>
                    <ButtonCus variant='default' size='sm' type='submit'>
                        {tAll('button.save')}
                    </ButtonCus>
                </div>
            </form>
        </Form>
    );
});
